import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "./RunwayView.css";

const mToFt = (m) => Math.round(m * 3.28084);

// A full-screen "on the field" view: satellite imagery of the airport with its
// runways traced and labelled, plus a readout of each runway's length & heading.
export default function RunwayView({ airport, onClose }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const layersRef = useRef(null);
  const [runways, setRunways] = useState([]);
  const [status, setStatus] = useState("Loading the field…");
  const [base, setBase] = useState("satellite"); // "satellite" | "map"

  // Build the map once, with both a satellite and a plain map base layer ready.
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true });
    map.setView([airport.lat, airport.lon], 14);

    const satellite = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { attribution: "Imagery &copy; Esri, Maxar, Earthstar Geographics", maxZoom: 19 }
    );
    const streets = L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        maxZoom: 20,
      }
    );
    layersRef.current = { satellite, map: streets };
    satellite.addTo(map); // default matches initial `base` state
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layersRef.current = null;
    };
  }, [airport]);

  // Swap the base layer when the user toggles Satellite / Map.
  useEffect(() => {
    const map = mapRef.current;
    const layers = layersRef.current;
    if (!map || !layers) return;
    const next = layers[base] || layers.satellite;
    const other = base === "satellite" ? layers.map : layers.satellite;
    if (map.hasLayer(other)) map.removeLayer(other);
    if (!map.hasLayer(next)) next.addTo(map);
  }, [base]);

  // Fetch and draw the runways.
  useEffect(() => {
    const controller = new AbortController();
    setStatus("Tracing runways…");
    setRunways([]);
    fetch(`/api/runways/${airport.icao}?lat=${airport.lat}&lon=${airport.lon}`, {
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const rws = data?.runways || [];
        setRunways(rws);
        const map = mapRef.current;
        if (!map) return;
        if (!rws.length) {
          setStatus("No runway map available for this airport — showing satellite view.");
          return;
        }
        setStatus("");
        const all = [];
        for (const rw of rws) {
          const line = L.polyline(rw.coords, {
            color: "#ffd166",
            weight: 4,
            opacity: 0.9,
          }).addTo(map);
          if (rw.ref) {
            line.bindTooltip(rw.ref, {
              permanent: true,
              direction: "center",
              className: "runway-label",
            });
          }
          all.push(...rw.coords);
        }
        try {
          map.fitBounds(L.latLngBounds(all).pad(0.25));
        } catch {
          /* keep default view */
        }
      })
      .catch((e) => {
        if (e.name !== "AbortError") setStatus("Couldn't load runway data.");
      });
    return () => controller.abort();
  }, [airport]);

  return (
    <div className="runway-view" role="dialog" aria-modal="true" aria-label={`Airport detail for ${airport.name}`}>
      <div className="rv-topbar">
        <div>
          <h2>{airport.name}</h2>
          <p className="rv-sub">
            {airport.icao}
            {airport.iata ? ` · ${airport.iata}` : ""} — airport detail &amp; runways
          </p>
        </div>
        <div className="rv-actions">
          <div className="rv-basetoggle" role="group" aria-label="Base map style">
            <button
              className={base === "satellite" ? "active" : ""}
              aria-pressed={base === "satellite"}
              onClick={() => setBase("satellite")}
            >
              🛰️ Satellite
            </button>
            <button
              className={base === "map" ? "active" : ""}
              aria-pressed={base === "map"}
              onClick={() => setBase("map")}
            >
              🗺️ Map
            </button>
          </div>
          <button className="rv-close" onClick={onClose} aria-label="Close airport detail">
            ✕ Close
          </button>
        </div>
      </div>

      <div className="rv-body">
        <div ref={containerRef} className="rv-map" />

        <aside className="rv-panel" aria-label="Runway list">
          <h3>Runways</h3>
          {status ? <p className="muted small">{status}</p> : null}
          {runways.map((rw) => (
            <div key={rw.ref || rw.heading} className="rv-runway">
              <div className="rv-runway-id">{rw.ref || "—"}</div>
              <div className="rv-runway-meta">
                <span>{mToFt(rw.length).toLocaleString()} ft</span>
                <span>{Math.round(rw.length).toLocaleString()} m</span>
                {rw.surface ? <span className="muted">{rw.surface}</span> : null}
              </div>
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}
